
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';

import ModalContent from '@/components/modal/ModalContent.vue';
import ModalFullScreen from '@/components/modal/ModalFullScreenNew.vue';
import UiButton from '@/components/button/ui-button.vue';
import GET_PROJECT_ACTIVITY_TIMES from '@/apollo/projectActivities/GetProjectActivityTimes.gql';
import APPROVE_PROJECT_ACTIVITY_TIME from '@/apollo/projectActivities/ApproveProjectActivityTime.gql';
import PROJECT_ACTIVITY_TIME_AGGREGATE from '@/apollo/projectActivities/ProjectActivityTimeAggregate.gql';
import { Project_Activity, Project_Activity_Time } from '@/generated/graphql';
import S3Image from '@/components/common/s3-image.vue';
import { NOTIFICATION } from '@/entities/Events';
import MoreActionsClick from '@/components/common/MoreActionsClick.vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';
import DeclineReasonModal from '@/components/projects/timeTracker/DeclineReasonModal.vue';
import convertSecondsToReadable from '@/utils/convertSecondsToReadable';
import SchedulerItemAvatar from '@/components/scheduler/SchedulerItemAvatar.vue';
import Badge from '@/components/common/BadgeNew.vue';
import BadgeColor from '@/entities/BadgeColor';

@Component({
	components: {
		ModalContent,
		UiButton,
		ModalFullScreen,
		S3Image,
		LoadingOverlay,
		MoreActionsClick,
		DeclineReasonModal,
		SchedulerItemAvatar,
    Badge,
    BadgeColor
	},
	apollo: {
		trackedTimes: {
			query: GET_PROJECT_ACTIVITY_TIMES,
			variables() {
				return {
					activityId: this.activity?.id,
				};
			},
			update: (data) => data.project_activity_time,
		},
		timeAggregate: {
			query: PROJECT_ACTIVITY_TIME_AGGREGATE,
			variables() {
				return {
					activityId: this.activity?.id,
				};
			},
			update: (data) =>
				data.project_activity_time_aggregate.aggregate.sum.time,
		},
	},
})
export default class ManagementTimeTrackerModal extends Vue {
	@Prop({ type: Object, required: true })
	readonly activity!: Project_Activity;

  BadgeColor: typeof BadgeColor = BadgeColor;

	timeAggregate: number = 0;
	trackedTimes: Project_Activity_Time[] = undefined;
	processing: boolean = false;
	selectedTimes: string[] = [];
	selectAll: boolean = false;
	checkedIds: { id: string; checked: boolean; status: string }[] = [];
	declineDisabled: boolean = true;
	approveDisabled: boolean = true;
	allApproved: boolean = false;
	showNotes: boolean = false;
	focusedOnTime: Project_Activity_Time = undefined;
	convertSecondsToReadable = convertSecondsToReadable;

	@Watch('trackedTimes', { immediate: true })
	checkIfAllApproved() {
		this.allApproved = true;
		this.trackedTimes.forEach((time) => {
			if (
				time.status.toLowerCase() !== 'approved' &&
				time.status.toLowerCase() !== 'declined'
			) {
				this.allApproved = false;
			}
		});
	}
	checkApprovedDisabled() {
		//if there's no checked items, it's disabeld, if there's one checked item, it's enabled, if there's more than one checked item, it's still enabled
		if (this.checkedIds.filter((item) => item.checked).length === 0) {
			this.approveDisabled = true;
		} else {
			this.approveDisabled = false;
		}
	}
	checkDeclineDisabled() {
		//if there's no checked items, it's disabeld, if there's one checked item, it's enabled, if there's more than one checked item, it's disabled
		if (
			this.checkedIds.filter((item) => item.checked).length === 0 ||
			this.checkedIds.filter((item) => item.checked).length > 1
		) {
			this.declineDisabled = true;
		} else {
			this.declineDisabled = false;
		}
	}
	toggleSelectAll() {
		this.selectAll = !this.selectAll;
		this.$nextTick(() => {
			this.checkedIds.forEach((item) => {
				if (
					item.status.toLowerCase() === 'pending' ||
					item.status.toLowerCase() === 'created'
				) {
					item.checked = this.selectAll;
				}
			});
			this.checkApprovedDisabled();
			this.checkDeclineDisabled();
		});
	}
	@Watch('trackedTimes', { immediate: true })
	makeCheckList() {
		this.checkedIds = this.trackedTimes.map((time) => ({
			id: time.id,
			checked: false,
			status: time.status,
		}));
	}
	convertDateString(date: string): string {
		// date = date.replace("-", "/").replace("-", "/");
		var parts = date.split('-');
		// return date;
		return parts[2] + '/' + parts[1] + '/' + parts[0];
	}
	getContractorInitials(user: any): string {
		if (!user) {
			return '';
		}
		// return (user.given_name ? user.given_name.trimStart()[0].toUpperCase() : "") + (user.family_name ? user.family_name.trimStart()[0].toUpperCase() : "");
		return (
			(user.given_name
				? user.given_name.trimStart()[0].toUpperCase() + ' '
				: '') +
			(user.family_name
				? user.family_name.trimStart()[0].toUpperCase()
				: '')
		);
	}
	closeModal(): void {
		this.$emit('close');
		// window.location.reload();
	}

	async approveTimes(): Promise<void> {
		this.processing = true;
		let approveIds = this.trackedTimes
			.filter((time, index) => {
				if (this.checkedIds[index].checked) {
					return time.id;
				}
			})
			.map((time) => {
				return time.id;
			});

		await this.$apollo.mutate({
			mutation: APPROVE_PROJECT_ACTIVITY_TIME,
			variables: { ids: approveIds },
		});

		await this.$apollo.queries.trackedTimes.refetch();
		await this.$apollo.queries.timeAggregate.refetch();

		this.processing = false;
		this.declineDisabled = false;
		this.$eventBus.emit(NOTIFICATION, {
			status: 'SUCCESS',
			message:
				`You approved ` +
				this.activity.contractor.user.given_name +
				` ` +
				this.activity.contractor.user.family_name +
				`'s submitted hours`,
			title: 'Succesfully approved hours',
			duration: 4000,
		});
		this.$emit('close');
	}
	get modalTitle(): string {
		return `${
			this.activity.title
		} | Total hours tracked: ${this.convertSecondsToReadable(
			this.timeAggregate
		)}`;
	}
	async declineTime(): Promise<void> {
		let time = this.trackedTimes.filter((time, index) => {
			if (this.checkedIds[index].checked) {
				return time;
			}
		})[0];
		this.$emit('openDeclineReasonModal', time);
	}
	async approveTime(time: Project_Activity_Time): Promise<void> {
		this.processing = true;
		this.declineDisabled = true;

		let approveIds = [];
		approveIds.push(time.id);

		await this.$apollo.mutate({
			mutation: APPROVE_PROJECT_ACTIVITY_TIME,
			variables: { ids: approveIds },
		});

		await this.$apollo.queries.trackedTimes.refetch();
		await this.$apollo.queries.timeAggregate.refetch();

		this.processing = false;
		this.declineDisabled = false;
	}

	toggleShowNotes(time: Project_Activity_Time): void {
		if (this.showNotes) {
			this.showNotes = false;
			this.focusedOnTime = undefined;
		} else {
			this.focusedOnTime = time;
			this.showNotes = true;
		}
	}
	mounted() {
		this.makeCheckList();
		this.checkIfAllApproved();
	}
}
